'use client';

import { useCallback, useEffect, useRef } from 'react';

const ALLOWED_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign'];

const RETRY_INTERVAL = 10000;
const MAX_ATTEMPTS = 5;

type URLParams = Record<string, string>;

export const useUTMToHelpCrunch = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);

  const sendToHelpCrunch = (params: URLParams) => {
    if (typeof window.HelpCrunch === 'function') {
      window.HelpCrunch('updateUserData', params);
      return true;
    }
    return false;
  };

  const trySendUTM = useCallback(() => {
    const params = getURLParams();
    const hasParams = Object.keys(params).length > 0;

    if (!hasParams) {
      return true;
    }

    const success = sendToHelpCrunch(params);

    if (success) {
      return true;
    }

    return false;
  }, []);

  const scheduleRetry = useCallback(() => {
    attemptsRef.current++;

    if (attemptsRef.current >= MAX_ATTEMPTS) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;

      if (!trySendUTM()) {
        scheduleRetry();
      }
    }, RETRY_INTERVAL);
  }, [trySendUTM]);

  useEffect(() => {
    attemptsRef.current = 0;

    if (!trySendUTM()) {
      scheduleRetry();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [trySendUTM, scheduleRetry]);
};

const getURLParams = (): URLParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params: URLParams = {};
  const urlParams = new URLSearchParams(window.location.search);

  urlParams.forEach((value, key) => {
    if (value && ALLOWED_PARAMS.includes(key)) {
      params[key] = value;
    }
  });

  return params;
};
